import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { faqList, testimonials, siteInfo } from '@/mocks/mangaData';


export default function AboutPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'h漫画',
    url: 'https://hmanga.com',
    description: siteInfo.description,
    email: siteInfo.email,
    telephone: siteInfo.phone,
    address: {
      '@type': 'PostalAddress',
      streetAddress: siteInfo.address,
      addressCountry: 'CN',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqList.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于h漫画 - 精选成人漫画在线阅读平台',
    description: '了解h漫画平台：品牌介绍、口碑评价、联系方式。安全便捷私密地享受优质漫画内容。',
    url: 'https://hmanga.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: 'h漫画',
      url: 'https://hmanga.com',
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }} />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

        <section className="w-full px-4 md:px-6 py-6 md:py-8">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-100 font-heading mb-2">关于h漫画</h1>
            <p className="text-sm text-foreground-500 mb-8">精选成人漫画在线阅读平台</p>

            <div className="bg-background-100 border border-background-300/60 rounded-lg p-5 md:p-6 mb-8">
              <h2 className="text-lg font-semibold text-foreground-200 font-heading mb-4">品牌介绍</h2>
              <p className="text-sm text-foreground-400 leading-relaxed mb-4">{siteInfo.description}</p>
              <p className="text-sm text-foreground-400 leading-relaxed">
                h漫画自{siteInfo.founded}成立以来，始终坚持以用户需求为导向，不断优化阅读体验和内容质量。
                我们的团队由资深漫画爱好者和技术专家组成，致力于打造国内领先的成人漫画阅读平台。
                平台现有{siteInfo.totalManga}部漫画作品，日活跃用户超过{siteInfo.dailyUsers}人，累计阅读量突破{siteInfo.totalViews}次。
                我们严格筛选内容来源，确保每一部作品都经过版权审核，为读者提供安全、优质的阅读环境。
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-8">
              {[
                { value: siteInfo.founded, label: '成立时间' },
                { value: siteInfo.totalManga, label: '漫画数量' },
                { value: siteInfo.dailyUsers, label: '日活用户' },
                { value: siteInfo.totalViews, label: '累计阅读' },
              ].map((stat) => (
                <div key={stat.label} className="bg-background-100 border border-background-300/60 rounded-lg p-4 text-center">
                  <p className="text-xl md:text-2xl font-bold text-accent-500 font-heading">{stat.value}</p>
                  <p className="text-xs text-foreground-500 mt-1">{stat.label}</p>
                </div>
              ))}
            </div>

            <div className="mb-8" id="testimonials">
              <h2 className="text-lg font-semibold text-foreground-200 font-heading mb-4">读者口碑</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                {testimonials.map((item, idx) => (
                  <div key={idx} className="bg-background-100 border border-background-300/60 rounded-lg p-4 md:p-5">
                    <div className="flex items-center gap-1 mb-2">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i
                          key={i}
                          className={`text-xs ${i < item.rating ? 'ri-star-fill text-accent-500' : 'ri-star-fill text-foreground-700'}`}
                        ></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-400 leading-relaxed mb-3">&ldquo;{item.content}&rdquo;</p>
                    <div>
                      <p className="text-sm font-medium text-foreground-200">{item.name}</p>
                      <p className="text-xs text-foreground-600">{item.role}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-8" id="faq">
              <h2 className="text-lg font-semibold text-foreground-200 font-heading mb-4">常见问题</h2>
              <div className="flex flex-col gap-2">
                {faqList.map((faq, idx) => (
                  <div key={idx} className="bg-background-100 border border-background-300/60 rounded-lg overflow-hidden">
                    <button
                      className="w-full flex items-center justify-between p-4 text-left cursor-pointer"
                      onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    >
                      <span className="text-sm font-medium text-foreground-200 pr-4">{faq.question}</span>
                      <i
                        className={`text-foreground-500 transition-transform duration-200 flex-shrink-0 ${
                          openFaq === idx ? 'ri-subtract-line' : 'ri-add-line'
                        }`}
                      ></i>
                    </button>
                    {openFaq === idx && (
                      <div className="px-4 pb-4">
                        <p className="text-sm text-foreground-400 leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-background-100 border border-background-300/60 rounded-lg p-5 md:p-6" id="contact">
              <h2 className="text-lg font-semibold text-foreground-200 font-heading mb-4">联系我们</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <p className="text-sm text-foreground-400 leading-relaxed mb-4">
                    如果您有任何问题或建议，欢迎通过以下方式与我们联系。
                    我们的客服团队将在24小时内回复您的消息。
                  </p>
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500/10 flex-shrink-0">
                        <i className="ri-mail-line text-primary-500"></i>
                      </div>
                      <span className="text-sm text-foreground-300">{siteInfo.email}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500/10 flex-shrink-0">
                        <i className="ri-phone-line text-primary-500"></i>
                      </div>
                      <span className="text-sm text-foreground-300">{siteInfo.phone}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500/10 flex-shrink-0">
                        <i className="ri-map-pin-line text-primary-500"></i>
                      </div>
                      <span className="text-sm text-foreground-300">{siteInfo.address}</span>
                    </div>
                  </div>
                </div>

                <ContactForm />
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}

function ContactForm() {
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setStatus('success');
      return;
    }

    formData.delete('phone_alt');

    const formUrl = 'https://readdy.ai/api/form/d9qsq3knlsngrm4lm5d0';
    try {
      const res = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try { parsed = JSON.parse(text); } catch { /* ignore */ }

      if (res.ok && parsed?.code === 'OK') {
        setStatus('success');
        setErrorMsg('');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || text || '';
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setStatus('error');
          setErrorMsg('提交失败，请稍后再试');
        } else {
          setStatus('error');
          setErrorMsg(serverMsg || '提交失败，请稍后重试');
        }
      }
    } catch {
      setStatus('error');
      setErrorMsg('网络错误，请检查连接后重试');
    }
  };

  return (
    <form onSubmit={handleSubmit} data-readdy-form="">
      <div className="flex flex-col gap-3">
        <input
          type="text"
          name="name"
          placeholder="您的姓名"
          required
          className="bg-background-200 border border-background-300/60 rounded-md px-3 py-2 text-sm text-foreground-200 placeholder-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
        />
        <input
          type="email"
          name="email"
          placeholder="您的邮箱"
          required
          className="bg-background-200 border border-background-300/60 rounded-md px-3 py-2 text-sm text-foreground-200 placeholder-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
        />
        <textarea
          name="message"
          placeholder="请输入您的留言（500字以内）"
          rows={3}
          maxLength={500}
          required
          className="bg-background-200 border border-background-300/60 rounded-md px-3 py-2 text-sm text-foreground-200 placeholder-foreground-600 focus:outline-none focus:border-primary-500 transition-colors resize-none"
        ></textarea>
        <input
          type="text"
          name="phone_alt"
          className="honeypot-field"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />
        <button
          type="submit"
          className="bg-primary-500 hover:bg-primary-600 text-background-50 px-5 py-2.5 rounded-md text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap"
        >
          发送消息
        </button>
        {status === 'success' && (
          <p className="text-xs text-accent-500">消息已发送成功，我们会尽快回复您！</p>
        )}
        {status === 'error' && errorMsg && (
          <p className="text-xs text-red-400">{errorMsg}</p>
        )}
      </div>
    </form>
  );
}